import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from './Container';
import { Colors } from '../constants/colors';
import { Device } from '../constants/device';
import { TRAINING_PATH } from '../constants/routes';
import { ArrowRightDiagonal } from './icons/ArrowRightDiagonal';
import { localizedPath } from '../helpers/localizedPath';
import { isIkemTrainingRegistrationOpen } from '../helpers/showsIkemTraining';
import { scrollToTop } from '../helpers/scrollToTop';

/**
 * Announces the training on the home page. The details live on the training
 * page, so the home page only carries this one prominent link to it.
 */
export const TrainingBanner = (): ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // Promising an open registration after the deadline has passed would send
  // visitors to a form nobody is reading any more.
  const label = isIkemTrainingRegistrationOpen() ? t('training.banner.label') : t('training.banner.labelSoon');

  return (
    <BannerSection>
      <Container>
        <BannerLink to={localizedPath(pathname, TRAINING_PATH)} onClick={scrollToTop}>
          <BannerText>
            <BannerLabel>{label}</BannerLabel>
            <BannerTitle>{t('section.training')}</BannerTitle>
            <BannerFacts>
              {t('training.whenAndWhere.date')} · {t('training.whenAndWhere.place')}
            </BannerFacts>
          </BannerText>
          <BannerCta>
            {t('training.banner.cta')}
            <ArrowRightDiagonal color={Colors.primary} />
          </BannerCta>
        </BannerLink>
      </Container>
    </BannerSection>
  );
};

const BannerSection = styled.section`
  /* Sits directly under the white jumbotron, so it continues its background.
     The jumbotron ends in a blue shape and the banner is blue too, so they need
     enough white between them not to read as one block. */
  background: ${Colors.white};
  padding: 90px 0 70px;

  ${Device.tablet} {
    padding: 50px 0 40px;
  }
`;

const BannerLink = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 40px;
  border-radius: 20px;
  background: ${Colors.primary};
  color: ${Colors.white};
  text-decoration: none;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid ${Colors.primaryDark};
    outline-offset: 3px;
  }

  ${Device.tablet} {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 25px;
  }
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const BannerLabel = styled.span`
  display: inline-flex;
  align-self: flex-start;
  background: ${Colors.white};
  color: ${Colors.primary};
  border-radius: 20px;
  padding: 3px 14px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 5px;
`;

const BannerTitle = styled.span`
  font-size: 26px;
  font-weight: 900;
  line-height: 1.3;

  ${Device.tablet} {
    font-size: 20px;
  }
`;

const BannerFacts = styled.span`
  font-size: 16px;
  line-height: 1.5;
`;

const BannerCta = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: none;
  background: ${Colors.white};
  color: ${Colors.primary};
  border-radius: 6px;
  padding: 0 30px;
  line-height: 50px;
  font-weight: bold;
  white-space: nowrap;

  ${Device.tablet} {
    width: 100%;
    white-space: normal;
    line-height: 1.4;
    padding: 15px 20px;
    text-align: center;
  }
`;
